import { DollarSign, Clock, MailSearch } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { DotGrid } from "@/components/marketing/DotGrid";

const problems = [
  {
    icon: DollarSign,
    text: "CRMs are expensive and complex",
    description: "Salesforce, HubSpot, Pipedrive — hundreds per month and weeks of onboarding.",
    stat: "$1,200/mo",
    statLabel: "Salesforce starter, per seat-tier",
  },
  {
    icon: Clock,
    text: "Manual contact work eats time",
    description: "Copy-pasting names, companies and phone numbers from emails kills focus every week.",
    stat: "4.2 hrs",
    statLabel: "average lost per professional / week",
  },
  {
    icon: MailSearch,
    text: "Contacts live in email threads",
    description: "Important people get buried in your inbox with no structure, no tags, no context.",
    stat: "73%",
    statLabel: "of B2B contacts never reach a CRM",
  },
];

const ProblemCard = ({ problem, index }: { problem: typeof problems[0]; index: number }) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: "slide-up",
    delay: index * 100,
    threshold: 0.15,
  });

  return (
    <div ref={ref} className={`fluent-tile fluent-tile-hover p-6 md:p-7 bg-card flex flex-col ${animationClass}`}>
      <div className="w-10 h-10 rounded-lg bg-surface-2 border border-border/60 grid place-items-center mb-5">
        <problem.icon className="w-5 h-5 text-foreground" strokeWidth={1.75} />
      </div>
      <h3 className="text-lg font-display font-semibold mb-2 text-foreground">{problem.text}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed mb-5">{problem.description}</p>
      <div className="mt-auto pt-4 border-t border-border/60">
        <div className="font-mono text-2xl font-semibold tracking-tight text-foreground">
          {problem.stat}
        </div>
        <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">
          {problem.statLabel}
        </div>
      </div>
    </div>
  );
};

export const Problem = () => {
  const { ref: titleRef, animationClass: titleAnimation } = useScrollAnimation<HTMLDivElement>({
    type: "fade-scale",
    threshold: 0.3,
  });

  return (
    <section className="relative py-20 md:py-28 bg-background overflow-hidden">
      <DotGrid fade="radial" size="sm" />
      <div className="container px-4 relative">
        <div ref={titleRef} className={`max-w-3xl mb-12 md:mb-16 ${titleAnimation}`}>
          <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
            The reality
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold mt-3 mb-5">
            Not everyone needs a full CRM.
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            Heavy CRM platforms are overkill for most professionals. You don't need pipelines, sales stages,
            or 50-page manuals. You need smart contact management in Outlook — the tool you already use every day.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-5">
          {problems.map((problem, index) => (
            <ProblemCard key={index} problem={problem} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
